import { TextInput, type TextInputProps } from '@mantine/core';
import type { ChangeEvent } from 'react';
import type { Except, Merge, SetRequired } from 'type-fest';
import { useCommonInputProps } from '../../../hooks/useCommonInputProps';
import type {
    Controlled_ValidatedInputProps,
    StringOrNullableString,
} from './types';

export type Controlled_StringInputProps<N extends boolean> = Merge<
    Except<
        SetRequired<TextInputProps, 'readOnly'>,
        'styles' | 'rightSectionWidth' | 'rightSection'
    >,
    Controlled_ValidatedInputProps<N, string>
>;

export const Controlled_StringInput = <N extends boolean>({
    nullable,
    value,
    onChange,
    label: _label,
    labelInfo,
    severity,
    helperText,
    endingIcon,
    w,
    placeholder: _placeholder,
    readOnly,
    nullableInReadOnly,
    ...props
}: Controlled_StringInputProps<N>) => {
    const { rightSectionWidth, rightSection, inputStyles, label, placeholder } =
        useCommonInputProps({
            endingIcon,
            severity,
            helperText,
            nullable,
            onChange,
            value,
            label: _label,
            labelInfo,
            placeholder: _placeholder,
            readOnly,
            nullableInReadOnly
        });

    const handleChange =
        onChange ?
            (event: ChangeEvent<HTMLInputElement>) => {
                const newValue = event.currentTarget.value;
                onChange(
                    nullable && !newValue ?
                        (null as StringOrNullableString<N>)
                    :   newValue
                );
            }
        :   undefined;

    return (
        <TextInput
            {...props}
            readOnly={readOnly}
            value={value ?? ''}
            w={w ?? '100%'}
            label={label}
            placeholder={placeholder}
            rightSectionWidth={rightSectionWidth}
            rightSection={rightSection}
            styles={{
                input: inputStyles,
                section: {
                    display: 'flex',
                    justifyContent: 'flex-end',
                },
            }}
            onChange={handleChange}
        />
    );
};
